import React, { useState } from 'react';
import researchService from '../../services/researchService';

/**
 * ApiBackfillSection — pulls structured quarterly financials from SEC EDGAR
 * (companyfacts XBRL) and upserts them into
 * DDB + Redshift in parallel with the existing LLM-based PDF extraction flow.
 *
 * Renders only when the company has a ticker symbol. Source rows are tagged
 * with source_doc_type="api_sec" so it's obvious in the UI where they came
 * from and so they don't collide with rows produced by uploaded documents.
 *
 * Props:
 *   company:    { company_id, name, ticker, ... }
 *   onComplete: optional callback invoked with the final job-status payload
 *               (so the parent dialog can refresh earnings lists, etc.)
 */
function ApiBackfillSection({ company, onComplete }) {
  const [unit, setUnit] = useState('quarters'); // 'quarters' | 'years'
  const [count, setCount] = useState(8);
  const [includeSec, setIncludeSec] = useState(false);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  const ticker = company?.ticker;
  const companyId = company?.company_id;

  const runBackfill = async () => {
    if (!companyId || !ticker || running) return;
    setRunning(true);
    setResult(null);
    try {
      const opts = { includeSecCustomMetrics: includeSec };
      if (unit === 'years') {
        opts.years = Number(count);
      } else {
        opts.quarters = Number(count);
      }
      const data = await researchService.apiBackfill(companyId, opts);
      setResult(data);
      if (onComplete) onComplete(data);
    } catch (err) {
      setResult({
        success: false,
        periods_written: [],
        periods_skipped: [],
        errors: [err?.response?.data?.detail || err.message || 'API backfill failed'],
      });
    } finally {
      setRunning(false);
    }
  };

  if (!ticker) {
    return (
      <div className="border rounded p-3 mt-4 bg-light">
        <h6 className="fw-semibold mb-1">
          <i className="bi bi-cloud-download me-2 text-primary" />
          Backfill from SEC EDGAR
        </h6>
        <p className="small text-muted mb-0">
          Add a ticker symbol to this company to enable API-based backfill.
        </p>
      </div>
    );
  }

  return (
    <div className="border rounded p-3 mt-4 bg-light">
      <h6 className="fw-semibold mb-1">
        <i className="bi bi-cloud-download me-2 text-primary" />
        Backfill from SEC EDGAR
      </h6>
      <p className="small text-muted mb-2">
        Pull standard quarterly financials (revenue, margins, balance-sheet,
        cash flow, EPS, shares outstanding) for <strong>{ticker} </strong>
        directly from SEC EDGAR XBRL companyfacts. Q4 is derived from FY − (Q1+Q2+Q3).
        Rows are tagged <code>api_sec</code> so they don&apos;t collide with
        extractions from uploaded documents — added fields merge in without
        overwriting existing values.
      </p>

      <div className="d-flex flex-wrap align-items-end gap-3 mb-2">
        <div>
          <label className="form-label small mb-1 d-block">How far back?</label>
          <div className="input-group input-group-sm" style={{ width: 220 }}>
            <input
              type="number"
              min={1}
              max={unit === 'years' ? 20 : 80}
              className="form-control"
              value={count}
              onChange={(e) => setCount(e.target.value)}
              disabled={running}
            />
            <select
              className="form-select"
              value={unit}
              onChange={(e) => setUnit(e.target.value)}
              disabled={running}
            >
              <option value="quarters">quarters</option>
              <option value="years">years</option>
            </select>
          </div>
        </div>

        <div className="form-check mb-1">
          <input
            type="checkbox"
            id={`api-backfill-sec-${companyId}`}
            className="form-check-input"
            checked={includeSec}
            onChange={(e) => setIncludeSec(e.target.checked)}
            disabled={running}
          />
          <label
            htmlFor={`api-backfill-sec-${companyId}`}
            className="form-check-label small"
            title="Try to backfill the company's tracked custom metrics from SEC XBRL facts (experimental — requires matching XBRL tags)"
          >
            Also try SEC custom metrics <span className="text-muted">(experimental)</span>
          </label>
        </div>

        <button
          type="button"
          className="btn btn-sm btn-primary"
          onClick={runBackfill}
          disabled={running || !Number(count)}
        >
          {running ? (
            <>
              <span className="spinner-border spinner-border-sm me-1" />
              Pulling from APIs…
            </>
          ) : (
            <>
              <i className="bi bi-cloud-download me-1" />
              Run API backfill
            </>
          )}
        </button>
      </div>

      {result && (
        <div
          className={`alert py-2 px-2 mb-0 small ${
            result.success && (result.errors || []).length === 0
              ? 'alert-success'
              : result.success
              ? 'alert-warning'
              : 'alert-danger'
          }`}
        >
          {result.success ? (
            <>
              <div>
                <strong>
                  Wrote {(result.periods_written || []).length} period(s)
                </strong>
                {result.action_counts && Object.keys(result.action_counts).length > 0 && (
                  <>
                    {' '}— {Object.entries(result.action_counts)
                      .map(([k, v]) => `${v} ${k}`)
                      .join(', ')}
                  </>
                )}
                .
              </div>
              {(result.periods_written || []).length > 0 && (
                <div className="text-muted">
                  Periods: {result.periods_written.join(', ')}
                </div>
              )}
              {(result.periods_skipped || []).length > 0 && (
                <div className="text-muted">
                  Skipped (no metrics): {result.periods_skipped.join(', ')}
                </div>
              )}
              {(result.errors || []).length > 0 && (
                <div className="mt-1">
                  <strong>Warnings:</strong> {result.errors.join('; ')}
                </div>
              )}
            </>
          ) : (
            <>
              <strong>API backfill failed.</strong>{' '}
              {(result.errors || []).join('; ') || 'Unknown error.'}
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default ApiBackfillSection;
